import type { FieldDefinition, WriteRejection } from "@estelle/domain";
import type { ReactNode } from "react";

import { renderCell } from "@/components/table/cells";

/**
 * A time on a comment has to read exactly as it does in every list — the same
 * IST day boundary, the same format. Handing a minimal `FieldDefinition` to
 * `renderCell` keeps one formatter in the application rather than a copy here
 * that drifts from it (the approach `modules/orders/format.tsx` takes, which
 * this module does not import so that neither depends on the other).
 */
const DATETIME_FIELD: FieldDefinition = {
  name: "datetime",
  label: "Date",
  type: "datetime",
};

export function dateTime(value: string | null): ReactNode {
  return renderCell(DATETIME_FIELD, value);
}

/** 1.4 MB, 820 KB — what a file's size reads as next to its name. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * A refusal as a sentence. A field error is already one — "At most 5000
 * characters." — and prefixing it with the field's name or the generic "Check
 * the highlighted fields" says less, not more.
 */
export function describeRejection(rejection: WriteRejection): string {
  if (rejection.kind === "invalid" && rejection.fields) {
    const problems = Object.values(rejection.fields);
    if (problems.length > 0) return problems.join(" ");
  }
  return rejection.message;
}
